import {
  DragDropModule,
  CdkDragDrop,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
// Lucide Icons
import {
  LucideAngularModule,
  GripVertical,
  Trash2,
  Plus,
  ChevronDown,
  SettingsIcon,
  ImagePlus,
} from 'lucide-angular';
import { SurveyListOptionService } from '../../survey-list-option/survey-list-option';
import {
  AutoSaveFormBase,
  FormGroupControlsOf,
} from '../../base-classes/base-form.class';
import { firstValueFrom } from 'rxjs';
import { SurveyListOption } from '@hyper-inferno/survey-elements-core';
import { AddBulkOptionsDialog } from './add-bulk-options-dialog/add-bulk-options-dialog';
import {
  AddListOptionImageDialog,
  OptionImageSettings,
} from './add-list-option-image-dialog/add-list-option-image-dialog';

@Component({
  selector: 'lib-list-option-manager',
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    LucideAngularModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  templateUrl: './list-option-manager.html',
  styleUrl: './list-option-manager.scss',
})
export class ListOptionManager extends AutoSaveFormBase<SurveyListOption> {
  extraButton = input<string | undefined>();

  override entityId = computed(() => this.activeItemId());
  override entityName = 'survey_list_option';
  activeListId = input.required<string | undefined>();
  private listOptionService = inject(SurveyListOptionService);
  readonly items = signal<SurveyListOption[]>([]);
  activeItemId = signal<string | number | null>(null);
  questionId = input<number | null>(null);
  surveyId = input<string | number | null | undefined>(null);
  readonly hasChanges = output<boolean>();
  private initialItems: SurveyListOption[] = [];
  override form = this.fb.group<FormGroupControlsOf<SurveyListOption>>({
    id: this.fb.control<any | undefined>(undefined),
    list_id: this.fb.control<any | undefined>(undefined),
    option_key: this.fb.control<number>(0),
    option_display: this.fb.control<any | undefined>(undefined),
    is_open_ended: this.fb.control<boolean>(false),
    is_mutually_exclusive: this.fb.control<boolean>(false),
    simple_props: this.fb.control<any>({}),
  });

  // Icons
  readonly GripIcon = GripVertical;
  readonly TrashIcon = Trash2;
  readonly PlusIcon = Plus;
  readonly ChevronDownIcon = ChevronDown;
  readonly SettingsIcon = SettingsIcon;
  readonly ImagePlusIcon = ImagePlus;
  protected override rebuildFormArrays(data: any): void {
    if (data?.id) {
      this.form.patchValue({ id: data.id }, { emitEvent: false });
    }
  }
  constructor() {
    super();

    // Fetch with listLid

    // Effect to fetch items when the list ID changes
    effect(async () => {
      const listId = this.activeListId();
      if (listId) {
        try {
          await this.reloadItems(listId);
          const response = await firstValueFrom(
            this.listOptionService.getListOptions(listId),
          );
          // Sort by rank before setting
          const sortedItems = (response || []).sort(
            (a, b) => (a.option_key ?? 0) - (b.option_key ?? 0),
          );
          this.items.set(sortedItems);
        } catch (error) {
          console.error('Error fetching options:', error);
        }
      } else {
        this.items.set([]);
        this.initialItems = [];
      }
    });
  }
  // options
  async addItem(isExclusive = false, isOpenEnded = false) {
    const listId = this.activeListId();

    if (!listId) {
      console.error('Cannot add item: No active list ID found.');
      return;
    }

    const newItem: SurveyListOption = {
      list_id: listId,
      option_display: this.getDefaultText(isExclusive, isOpenEnded) ?? '',
      is_open_ended: isOpenEnded,
      is_mutually_exclusive: isExclusive,
      option_key: this.items().length + 1,
      id: '',
    };

    try {
      const response = await firstValueFrom(
        this.listOptionService.createListOption({
          list_id: listId,
          option_key: newItem.option_key ?? 0,
          option_display: newItem.option_display ?? '',
          is_mutually_exclusive: newItem.is_mutually_exclusive ?? false,
          is_open_ended: newItem.is_open_ended ?? false,
        }),
      );

      if (response?.id) {
        newItem.id = response.id;
        this.items.update((prev) => {
          const updated = [...prev, newItem];
          return updated;
        });
        this.listOptionService.notifyListUpdated(listId);
        this.checkForChanges();
      }
    } catch (error) {
      console.error('Error creating option:', error);
    }
  }

  private async saveOptionImageSettings(
    item: SurveyListOption,
    result: OptionImageSettings,
  ): Promise<void> {
    if (!item.id) {
      console.error('Cannot save image settings: option ID is missing.');
      return;
    }

    const listId = this.activeListId();

    if (!listId) {
      console.error('Cannot save image settings: no active list.');
      return;
    }

    // Use the actual survey_element.id for element-specific settings.
    const elementId = this.questionId();

    if (result.scope === 'element' && elementId == null) {
      console.error(
        'Cannot save element-specific settings: survey element ID is missing.',
      );
      return;
    }

    // Handle image removal before upload/save logic.
    if (result.imageRemoved && result.source === 'upload') {
      const targetElementId = result.scope === 'global' ? null : elementId;

      let deleteQuery = this.supabase.client
        .from('survey_list_option_render_config')
        .delete()
        .eq('survey_list_option_id', item.id)
        .eq('render_type', 'image');

      deleteQuery =
        targetElementId == null
          ? deleteQuery.is('element_id', null)
          : deleteQuery.eq('element_id', targetElementId);

      const { error } = await deleteQuery;

      if (error) {
        console.error('Failed to remove option image:', error.message);
        return;
      }

      console.log(`Option image removed successfully (${result.scope}).`);
      return;
    }

    let imageUrl: string | null = null;
    let customHtml: string | null = null;

    if (result.source === 'upload') {
      if (result.file) {
        let surveyId = this.surveyId();

        // Resolve survey_id from the current element when not provided
        if (!surveyId && elementId != null) {
          const { data: elementData, error: elementError } =
            await this.supabase.client
              .from('survey_element')
              .select('survey_id')
              .eq('id', elementId)
              .maybeSingle();

          if (elementError) {
            console.error('Failed to resolve survey ID:', elementError);
            return;
          }

          surveyId = elementData?.survey_id;
        }

        if (!surveyId) {
          console.error(
            'Survey ID is missing. Pass surveyId from the parent component for global uploads.',
          );
          return;
        }

        // Extract extension safely and build path
        const extension = result.file.name.includes('.')
          ? result.file.name.split('.').pop()
          : 'png';
        const fileName = `${Date.now()}_${crypto.randomUUID()}.${extension}`;

        const filePath = `${surveyId}/list/${listId}/${item.id}/${fileName}`;

        const { data, error } = await this.supabase.client.storage
          .from('console-upload-assets')
          .upload(filePath, result.file, {
            contentType: result.file.type,
            upsert: false,
          });

        if (error) {
          console.error('Image upload failed:', error.message);
          return;
        }

        const { data: publicUrlData } = this.supabase.client.storage
          .from('console-upload-assets')
          .getPublicUrl(data.path);

        imageUrl = publicUrlData.publicUrl;
      } else {
        // Reuse the image URL when editing existing settings.
        imageUrl = result.existingImageUrl ?? null;
      }

      if (!imageUrl) {
        console.error('Cannot save: image file or URL is missing.');
        return;
      }
    } else {
      customHtml = result.html?.trim() || null;

      if (!customHtml) {
        console.error('Cannot save: custom HTML is empty.');
        return;
      }
    }

    // Global settings use NULL; element settings use the real element ID.
    const targetElementId = result.scope === 'global' ? null : elementId;

    const config = {
      survey_list_option_id: item.id,
      element_id: targetElementId,
      render_type: result.source === 'upload' ? 'image' : 'html',
      image_url: imageUrl,
      custom_html: customHtml,
      show_option_text: result.showOptionText ?? true,
      updated_at: new Date().toISOString(),
    };

    const table = this.supabase.client.from('survey_list_option_render_config');

    let existingQuery = table.select('id').eq('survey_list_option_id', item.id);

    existingQuery =
      targetElementId == null
        ? existingQuery.is('element_id', null)
        : existingQuery.eq('element_id', targetElementId);

    const { data: existing, error: selectError } =
      await existingQuery.maybeSingle();

    if (selectError) {
      console.error(
        'Could not check existing configuration:',
        selectError.message,
      );
      return;
    }

    const { error: saveError } = existing?.id
      ? await this.supabase.client
          .from('survey_list_option_render_config')
          .update(config)
          .eq('id', existing.id)
      : await this.supabase.client
          .from('survey_list_option_render_config')
          .insert(config);

    if (saveError) {
      console.error('Could not save image settings:', saveError.message);
      return;
    }

    console.log(`Option image settings saved successfully (${result.scope}).`);
  }

  async handleAddImage(item: SurveyListOption): Promise<void> {
    const optionId = item.id;
    if (!optionId) {
      console.error('Option ID is missing.');
      return;
    }

    const listId = this.activeListId();
    if (!listId) {
      console.error('List ID is missing.');
      return;
    }

    const elementId = this.questionId();

    try {
      let surveyId = this.surveyId();

      // Resolve survey_id from the current survey element when available.
      if (elementId != null) {
        const { data: elementData, error: elementError } =
          await this.supabase.client
            .from('survey_element')
            .select('survey_id')
            .eq('id', elementId)
            .maybeSingle();

        if (elementError) {
          console.error('Failed to resolve survey ID:', elementError);
          return;
        }

        surveyId ??= elementData?.survey_id;
      }

      const client = this.supabase.client.from(
        'survey_list_option_render_config',
      );

      const globalQuery = client
        .select('render_type, image_url, custom_html, show_option_text')
        .eq('survey_list_option_id', optionId)
        .is('element_id', null)
        .maybeSingle();

      const elementQuery =
        elementId != null
          ? client
              .select('render_type, image_url, custom_html, show_option_text')
              .eq('survey_list_option_id', optionId)
              .eq('element_id', elementId)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null });

      const [globalResult, elementResult] = await Promise.all([
        globalQuery,
        elementQuery,
      ]);

      if (globalResult.error || elementResult.error) {
        console.error(
          'Failed to load option settings:',
          globalResult.error ?? elementResult.error,
        );
        return;
      }

      const dialogRef = this.dialog.open(AddListOptionImageDialog, {
        width: '600px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        autoFocus: false,
        data: {
          optionId: String(optionId),
          optionDisplay: item.option_display ?? '',
          questionId: elementId,
          surveyId,
          existingGlobal: globalResult.data,
          existingElement: elementResult.data,
        },
      });

      const result = (await firstValueFrom(dialogRef.afterClosed())) as
        | OptionImageSettings
        | undefined;

      if (result) {
        await this.saveOptionImageSettings(item, result);
      }
    } catch (error) {
      console.error('Failed to open option settings:', error);
    }
  }

  async removeItem(id: string) {
    try {
      const activeListId = this.activeListId();
      if (!activeListId) return;

      const payload = {
        id: id,
        list_id: activeListId,
      };

      // 1. Delete the item from the database
      await firstValueFrom(this.listOptionService.deleteListOption(payload));

      // 2. Filter out the deleted item and re-calculate option_keys sequentially
      const remainingItems = this.items().filter((item) => item.id !== id);
      const updatedItems = remainingItems.map((item, index) => ({
        ...item,
        option_key: index + 1, // Reset sequence starting from 1
      }));

      // 3. Update local Signal immediately for a responsive UI
      this.items.set(updatedItems);
      this.checkForChanges();

      // 4. Batch-update the new option_keys to the backend
      const optionKeyPayload = updatedItems.map((i) => ({
        id: i.id!,
        option_key: i.option_key!,
        listId: activeListId,
      }));

      await firstValueFrom(
        this.listOptionService.updateOptionRanks(optionKeyPayload),
      );

      // 5. Notify list updates
      this.listOptionService.notifyListUpdated(activeListId);
    } catch (error) {
      console.error('Error deleting option or updating ranks:', error);
    }
  }

  private getDefaultText(
    isExclusive: boolean,
    isOpenEnded: boolean,
  ): string | null {
    if (isExclusive) {
      return 'None of the above';
    } else if (isOpenEnded) {
      return 'Other (please specify)';
    } else {
      return null;
    }
  }

  async drop(event: CdkDragDrop<SurveyListOption[]>) {
    // 1. Move the item in the local array
    const currentItems = [...this.items()];
    moveItemInArray(currentItems, event.previousIndex, event.currentIndex);

    // 2. Re-calculate option_keys based on new array positions
    const updatedItems = currentItems.map((item, index) => ({
      ...item,
      option_key: index + 1, // Using 1-based indexing for option_key
    }));

    // 3. Update local Signal immediately for UI responsiveness
    this.items.set(updatedItems);
    this.checkForChanges();
    // 4. Persist to backend
    try {
      // Send only the necessary data to the service
      const option_keyPayload = updatedItems.map((i) => ({
        id: i.id!,
        option_key: i.option_key!,
        listId: this.activeListId()!,
      }));
      await firstValueFrom(
        this.listOptionService.updateOptionRanks(option_keyPayload),
      );
      this.listOptionService.notifyListUpdated(this.activeListId()!);
    } catch (error) {
      console.error('Failed to update option_keys:', error);
      // Optional: Fetch the original list again to revert the UI on failure
    }
  }
  setActiveOption(item: SurveyListOption) {
    if (this.saving) {
      return;
    }

    // Set the active item ID
    this.activeItemId.set(item.id ?? null);
    // Patch the form so AutoSaveFormBase knows which record is being edited
    this.form.patchValue(
      {
        id: item.id,
        list_id: item.list_id,
        option_display: item.option_display,
        is_open_ended: item.is_open_ended,
        is_mutually_exclusive: item.is_mutually_exclusive,
        option_key: item.option_key,
      },
      { emitEvent: false },
    );
    // focus
    requestAnimationFrame(() => {
      const input = document.querySelector(
        `input[formControlName="option_display"][data-item-id="${item.id}"]`,
      ) as HTMLInputElement;

      if (input) {
        input.focus();
        // Place cursor at the end
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    });
  }

  clearActiveOption() {
    this.activeItemId.set(null);
  }

  override PostCreate() {
    // 1. Get the latest data from the form (which now has the server-provided ID)
    const savedItem = this.form.getRawValue() as SurveyListOption;

    if (savedItem.id) {
      // 2. Update the signal so the UI reflects the "Saved" state and the real ID
      this.items.update((currentItems) =>
        currentItems.map((item) => {
          // If we find an item without an ID (the one we just added),
          // or if we match by some other temporary key, swap it.
          // Usually, the 'active' item is the one we just created.
          if (
            item.id === null ||
            item.id === undefined ||
            item.id === savedItem.id
          ) {
            return { ...item, ...savedItem };
          }
          return item;
        }),
      );

      // 3. Set this as the active item so the user can keep typing
      this.activeItemId.set(savedItem.id);
      this.checkForChanges();
    }
  }

  async handleEnterKey(event: any) {
    event.preventDefault(); // stops default button trigger
    event.stopPropagation(); // stops bubbling

    // 1. Force the current form to save immediately
    await this.bypassDebounceAndSave();

    const currentItems = this.items();
    const currentActiveId = this.form.value.id;

    // Find where the user is currently typing
    const currentIndex = currentItems.findIndex(
      (item) => item.id === currentActiveId,
    );

    // Check if the user is on the very last element (or if no item is active yet)
    const isLastElement =
      currentIndex === -1 || currentIndex === currentItems.length - 1;

    if (!isLastElement) {
      // --- CASE 1: Move to the next existing element ---
      const nextItem = currentItems[currentIndex + 1];
      if (nextItem) {
        this.setActiveOption(nextItem);
      }
    } else {
      // --- CASE 2: Create a new element (Your existing logic) ---
      this.addItem(false, false).then(() => {
        // Recursive function to attempt focusing the new item
        const tryToFocus = () => {
          const items = this.items();
          const newItem = items[items.length - 1];
          if (!newItem) return;

          this.setActiveOption(newItem);
          this.cdr.detectChanges();

          setTimeout(() => {
            const editableInput = document.querySelector(
              `input[formControlName="option_display"][data-item-id="${newItem.id}"]`,
            ) as HTMLInputElement;

            if (editableInput) {
              editableInput.focus();
              editableInput.setSelectionRange(
                editableInput.value.length,
                editableInput.value.length,
              );
              return;
            }

            const readonlyInput = document.querySelector(
              `input[data-item-id="${newItem.id}"]`,
            ) as HTMLInputElement;

            if (readonlyInput) {
              readonlyInput.dispatchEvent(
                new FocusEvent('focus', { bubbles: true }),
              );
              readonlyInput.click();
              setTimeout(() => tryToFocus(), 100);
            }
          }, 0);
        };
        setTimeout(() => tryToFocus(), 100);
      });
    }
  }

  private checkForChanges() {
    const currentItems = this.items();
    const hasChanges =
      JSON.stringify(currentItems) !== JSON.stringify(this.initialItems);
    this.hasChanges.emit(hasChanges);
  }

  private saveInitialState(items: SurveyListOption[]) {
    this.initialItems = JSON.parse(JSON.stringify(items));
    this.checkForChanges();
  }
  resetChangeTracking() {
    this.saveInitialState(this.items());
  }
  public async reloadItems(listId: string) {
    try {
      const response = await firstValueFrom(
        this.listOptionService.getListOptions(listId),
      );
      const sortedItems = (response || []).sort(
        (a, b) => (a.option_key ?? 0) - (b.option_key ?? 0),
      );
      this.items.set(sortedItems);
      this.saveInitialState(sortedItems);
      this.activeItemId.set(null);
      this.form.reset({}, { emitEvent: false });
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error reloading items:', error);
    }
  }
  public refreshCurrentList() {
    const currentListId = this.activeListId();
    if (currentListId) {
      this.reloadItems(currentListId);
    }
  }

  readonly dialog = inject(MatDialog);
  async addBulkOptions() {
    const listId = this.activeListId();
    if (!listId) return;

    const dialogRef = this.dialog.open(AddBulkOptionsDialog, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe(async (result: string) => {
      if (result) {
        // Split by new line and filter out empty strings
        const lines = result
          .split('\n')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);

        try {
          // Update your service call to pass the options
          const response = await firstValueFrom(
            this.listOptionService.createBulkOptions(listId, lines),
          );

          if (response) {
            await this.reloadItems(listId); // Refresh the UI with new items
            this.listOptionService.notifyListUpdated(listId);
          }
        } catch (error) {
          console.error('Error creating bulk options:', error);
        }
      }
    });
  }

  readonly configurationSettingEvent = output<string>();
  handleConfigurationSetting(id: string) {
    this.configurationSettingEvent.emit(id);
  }
}
